// Driving HUD (RALLY_GOAL.md §13): stage time, split deltas, penalties, stage progress, speed, revs
// with shift lights and gear, countdown lights, banners and notices. Every element can be switched
// off. Values that change while driving (time, speed, gear, revs, shift lights, progress) are drawn on
// canvases (./glyphs): DOM text and style writes every frame fed Blink's garbage-collected heap enough
// to force a full GC every ~30 s. The rest (splits, banners, notices) are DOM, written only on events.
import type { HudToggles } from '../game/settings';
import { formatDelta, formatTime } from '../game/timing';
import { cssVar, GlyphText, RectCanvas } from './glyphs';

const LEDS = 10;
/** Shift lights run from this rpm to the limiter (same as the dashboard display). */
const LED_FROM = 4500;
const LED_GAP = 4;
const LED_OFF = '#22272e';
const LED_ON = ['#2fe26b', '#2fe26b', '#2fe26b', '#2fe26b', '#ffc21a', '#ffc21a', '#ffc21a', '#ff3030', '#ff3030', '#ff3030'];
/** G-meter: the outer ring is G_MAX; rings at 0.5 g steps; a fading trail of the last positions. */
const G_MAX = 1.5;
const G_RINGS = [0.5, 1.0, 1.5];
const G_TRAIL = 24;
/** Redraw at most this often (ms): a smooth dot without a full canvas redraw every frame. */
const G_DRAW_MS = 33;
/** Pre-built labels (0.00 G … 3.00 G) so drawing the value makes no strings. */
const G_LABELS = Array.from({ length: 61 }, (_, i) => `${(i * 0.05).toFixed(2)} G`);

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, parent?: HTMLElement, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = cls;
  if (text !== undefined) e.textContent = text;
  parent?.appendChild(e);
  return e;
}

export interface HudFrame {
  /** Stage time (s), penalties (s). */
  time: number;
  penalties: number;
  speed: number;
  rpm: number;
  gear: number;
  /** Distance along the stage (m). */
  s: number;
  /** Lateral g (+ = accelerating to the left) and longitudinal g (+ = accelerating forward). */
  gLat: number;
  gLong: number;
  cockpit: boolean;
  /** performance.now() (ms) for blinking. */
  now: number;
}

export class Hud {
  readonly root: HTMLDivElement;
  private readonly timePanel: HTMLDivElement;
  private readonly timeText: HTMLDivElement;
  private readonly clock: GlyphText;
  private readonly penText: HTMLDivElement;
  private readonly splitRow: HTMLDivElement;
  private readonly splitLabel: HTMLSpanElement;
  private readonly splitValue: HTMLSpanElement;
  private readonly progress: HTMLDivElement;
  private readonly progressBar: RectCanvas;
  private readonly splitAt: number[];
  private readonly cluster: HTMLDivElement;
  private readonly speedText: HTMLDivElement;
  private readonly speedGlyphs: GlyphText;
  private readonly unitText: HTMLDivElement;
  private readonly gearText: HTMLDivElement;
  private readonly gearGlyphs: GlyphText;
  private readonly revHost: HTMLDivElement;
  private readonly revBar: RectCanvas;
  private readonly ledHost: HTMLDivElement;
  private readonly ledBar: RectCanvas;
  private readonly gHost: HTMLDivElement;
  private readonly gMeter: RectCanvas;
  /** Trail of dot positions (g units, x then y), a ring buffer. */
  private readonly gTrail = new Float32Array(G_TRAIL * 2);
  private gHead = 0;
  private gCount = 0;
  private gDrawnAt = -1e9;
  private readonly colors: { text: string; bad: string; accent: string; ink: string };
  private readonly lights: HTMLDivElement;
  private readonly lamps: HTMLSpanElement[] = [];
  private readonly bannerEl: HTMLDivElement;
  private readonly notices: HTMLDivElement;
  private readonly promptEl: HTMLDivElement;
  private toggles: HudToggles = { speed: true, revs: true, time: true, splits: true, progress: true, penalties: true, inCockpit: true, gforce: true };
  private mph = false;
  private last = { cs: -1, pen: '', speedV: -1, gearI: -2, rev: -1, hot: false, led: -1, prog: -1, cockpit: false };
  private splitUntil = 0;
  private bannerUntil = 0;
  private readonly startS: number;
  private readonly finishS: number;

  constructor(parent: HTMLElement, layout: { startS: number; finishS: number; splits: number[] }, private readonly limiter: number) {
    this.startS = layout.startS;
    this.finishS = layout.finishS;
    this.root = el('div', 'hud', parent);
    this.root.setAttribute('aria-hidden', 'true');
    // Time, splits, penalties, progress (top left).
    this.timePanel = el('div', 'hud-time', this.root);
    const tRow = el('div', 'hud-row', this.timePanel);
    el('span', 'hud-label', tRow, 'STAGE');
    this.timeText = el('div', 'hud-clock', tRow);
    this.clock = new GlyphText(this.timeText, '0123456789:.-–', 8, 'left');
    this.penText = el('div', 'hud-pen', this.timePanel);
    this.splitRow = el('div', 'hud-split', this.timePanel);
    this.splitLabel = el('span', 'hud-label', this.splitRow);
    this.splitValue = el('span', 'hud-split-value', this.splitRow);
    this.progress = el('div', 'hud-progress', this.timePanel);
    // The car marker (6×16 with a 2 px outline) overhangs the 6 px bar.
    this.progressBar = new RectCanvas(this.progress, { x: 5, y: 7 });
    this.splitAt = layout.splits.map((sp) => (sp - this.startS) / (this.finishS - this.startS));
    // Cluster (bottom right).
    this.cluster = el('div', 'hud-cluster', this.root);
    this.ledHost = el('div', 'hud-leds', this.cluster);
    this.ledBar = new RectCanvas(this.ledHost);
    const main = el('div', 'hud-main', this.cluster);
    const sp = el('div', 'hud-speed', main);
    this.speedText = el('div', 'hud-speed-value', sp);
    this.speedGlyphs = new GlyphText(this.speedText, '0123456789', 3, 'right');
    this.unitText = el('div', 'hud-speed-unit', sp, 'KM/H');
    this.gearText = el('div', 'hud-gear', main);
    this.gearGlyphs = new GlyphText(this.gearText, '0123456789NR', 1, 'center');
    this.revHost = el('div', 'hud-rev', this.cluster);
    this.revBar = new RectCanvas(this.revHost);
    // G-meter (bottom left: clear of the co-driver's tablet in the cockpit view).
    const gPanel = el('div', 'hud-gmeter', this.root);
    this.gHost = el('div', 'hud-gmeter-plot', gPanel);
    this.gMeter = new RectCanvas(this.gHost);
    this.colors = {
      text: cssVar(this.root, '--text', '#f4f6f8'),
      bad: cssVar(this.root, '--bad', '#ff3b30'),
      accent: cssVar(this.root, '--accent', '#e8381b'),
      ink: cssVar(this.root, '--ink', '#0b0d10'),
    };
    // Countdown lights (centre), banner, notices, prompt.
    this.lights = el('div', 'hud-lights', this.root);
    for (let i = 0; i < 5; i++) this.lamps.push(el('span', 'hud-lamp', this.lights));
    this.bannerEl = el('div', 'hud-banner', this.root);
    this.notices = el('div', 'hud-notices', this.root);
    this.promptEl = el('div', 'hud-prompt', this.root);
    this.setLights(0, false, false);
  }

  setVisible(on: boolean): void {
    this.root.classList.toggle('off', !on);
  }

  setToggles(t: HudToggles): void {
    this.toggles = { ...t };
    this.timeText.parentElement!.style.display = t.time ? '' : 'none';
    this.progress.style.display = t.progress ? '' : 'none';
    this.applyCluster(this.last.cockpit);
    if (!t.splits) this.splitRow.classList.remove('on');
    if (!t.penalties) this.penText.textContent = '';
    this.last.pen = '';
  }

  setUnits(mph: boolean): void {
    this.mph = mph;
    this.unitText.textContent = mph ? 'MPH' : 'KM/H';
    this.last.speedV = -1;
  }

  private applyCluster(cockpit: boolean): void {
    const show = !cockpit || this.toggles.inCockpit;
    this.cluster.style.display = show && (this.toggles.speed || this.toggles.revs) ? '' : 'none';
    this.speedText.parentElement!.style.display = this.toggles.speed ? '' : 'none';
    this.gearText.style.display = this.toggles.revs ? '' : 'none';
    this.revHost.style.display = this.toggles.revs ? '' : 'none';
    this.ledHost.style.display = this.toggles.revs ? '' : 'none';
    this.gHost.parentElement!.style.display = show && this.toggles.gforce ? '' : 'none';
    this.gDrawnAt = -1e9;
    // Shown again: redraw everything at the (possibly new) size.
    this.last.speedV = this.last.rev = this.last.led = -1;
    this.last.gearI = -2;
  }

  /** Canvas bars are sized from their hosts when first visible and after a DPR change. */
  private refit(bar: RectCanvas, visible: boolean): boolean {
    return visible && bar.stale() && bar.fit();
  }

  private drawLeds(lit: number): void {
    const b = this.ledBar;
    const c = b.ctx;
    c.clearRect(0, 0, b.w, b.h);
    const cell = (b.w - LED_GAP * (LEDS - 1)) / LEDS;
    for (let i = 0; i < LEDS; i++) {
      c.fillStyle = i < lit ? LED_ON[i] : LED_OFF;
      c.fillRect(Math.round(i * (cell + LED_GAP)), 0, Math.round(cell), b.h);
    }
  }

  private drawRev(rev: number, hot: boolean): void {
    const b = this.revBar;
    const c = b.ctx;
    c.clearRect(0, 0, b.w, b.h);
    c.fillStyle = hot ? this.colors.bad : this.colors.text;
    c.fillRect(0, 0, b.w * rev, b.h);
  }

  /** The G-meter: rings, crosshair, the trail and the dot (the force felt: braking moves it up, a
   * left-hand turn moves it right), and the combined g. */
  private drawG(gx: number, gy: number): void {
    const b = this.gMeter;
    const c = b.ctx;
    const w = b.w;
    const h = b.h;
    // Hidden (touch layout, or not laid out yet): nothing to draw, and arc() rejects a negative radius.
    if (w < 24 || h < 40) return;
    c.clearRect(0, 0, w, h);
    const cx = w / 2;
    const cy = (h - 18) / 2 + 2;
    const R = Math.min(w, h - 18) / 2 - 3;
    c.lineWidth = 1;
    c.strokeStyle = 'rgba(255, 255, 255, 0.34)';
    for (let i = 0; i < G_RINGS.length; i++) {
      c.beginPath();
      c.arc(cx, cy, (R * G_RINGS[i]) / G_MAX, 0, Math.PI * 2);
      c.stroke();
    }
    c.fillStyle = 'rgba(255, 255, 255, 0.3)';
    c.fillRect(cx - R, cy, R * 2, 1);
    c.fillRect(cx, cy - R, 1, R * 2);
    // Trail, oldest faintest.
    const k = R / G_MAX;
    c.fillStyle = this.colors.accent;
    for (let i = 0; i < this.gCount; i++) {
      const j = (this.gHead - 1 - i + G_TRAIL) % G_TRAIL;
      c.globalAlpha = 0.5 * (1 - i / G_TRAIL);
      c.fillRect(cx + this.gTrail[j * 2] * k - 1.5, cy + this.gTrail[j * 2 + 1] * k - 1.5, 3, 3);
    }
    c.globalAlpha = 1;
    // The dot.
    c.beginPath();
    c.arc(cx + gx * k, cy + gy * k, 5, 0, Math.PI * 2);
    c.fillStyle = this.colors.text;
    c.fill();
    c.strokeStyle = this.colors.accent;
    c.lineWidth = 2;
    c.stroke();
    // Combined g.
    const g = Math.min(60, Math.round(Math.hypot(gx, gy) * 20));
    c.font = "700 14px 'Barlow Condensed', 'Arial Narrow', sans-serif";
    c.textAlign = 'center';
    c.textBaseline = 'bottom';
    c.fillStyle = this.colors.text;
    c.fillText(G_LABELS[g], cx, h);
  }

  private drawProgress(p: number): void {
    const b = this.progressBar;
    const c = b.ctx;
    const x0 = 5;
    const y0 = 7;
    const w = b.w - 10;
    c.clearRect(0, 0, b.w, b.h);
    c.fillStyle = this.colors.accent;
    c.fillRect(x0, y0, w * p, 6);
    c.fillStyle = this.colors.text;
    for (const t of this.splitAt) c.fillRect(x0 + w * t, y0 - 3, 2, 12);
    const cx = x0 + w * p;
    c.fillStyle = this.colors.ink;
    c.fillRect(cx - 5, y0 - 7, 10, 20);
    c.fillStyle = this.colors.text;
    c.fillRect(cx - 3, y0 - 5, 6, 16);
  }

  update(f: HudFrame): void {
    const L = this.last;
    if (f.cockpit !== L.cockpit) {
      L.cockpit = f.cockpit;
      this.applyCluster(f.cockpit);
    }
    const cs = Math.round(f.time * 100);
    if (cs !== L.cs) {
      L.cs = cs;
      this.clock.set(formatTime(f.time));
    }
    const pen = this.toggles.penalties && f.penalties > 0 ? `+${f.penalties.toFixed(0)} s PENALTY` : '';
    if (pen !== L.pen) this.penText.textContent = L.pen = pen;
    const v = Math.round(Math.abs(f.speed) * (this.mph ? 2.23694 : 3.6));
    if (v !== L.speedV) {
      L.speedV = v;
      this.speedGlyphs.set(String(v));
    }
    const gi = f.gear < 0 ? -1 : f.gear;
    if (gi !== L.gearI) {
      L.gearI = gi;
      this.gearGlyphs.set(gi < 0 ? 'R' : gi === 0 ? 'N' : String(gi));
    }
    const revsOn = this.toggles.revs && (!f.cockpit || this.toggles.inCockpit);
    if (this.refit(this.revBar, revsOn)) L.rev = -1;
    if (this.refit(this.ledBar, revsOn)) L.led = -1;
    const rev = Math.round(Math.min(1, f.rpm / this.limiter) * 200) / 200;
    const hot = f.rpm > this.limiter - 400;
    if (revsOn && (rev !== L.rev || hot !== L.hot)) {
      L.rev = rev;
      L.hot = hot;
      this.drawRev(rev, hot);
    }
    const flash = f.rpm > this.limiter - 150 && Math.floor(f.now / 80) % 2 === 0;
    const lit = flash ? 0 : Math.max(0, Math.min(LEDS, Math.ceil(((f.rpm - LED_FROM) / (this.limiter - LED_FROM)) * LEDS)));
    if (revsOn && lit !== L.led) {
      L.led = lit;
      this.drawLeds(lit);
    }
    const gOn = this.toggles.gforce && (!f.cockpit || this.toggles.inCockpit);
    if (this.refit(this.gMeter, gOn)) this.gDrawnAt = -1e9;
    if (gOn && f.now - this.gDrawnAt >= G_DRAW_MS) {
      this.gDrawnAt = f.now;
      // Felt force: opposite to the acceleration (left turn → right, braking → up), clamped to the ring.
      let gx = -f.gLat;
      let gy = f.gLong;
      const m = Math.hypot(gx, gy);
      if (m > G_MAX) {
        gx *= G_MAX / m;
        gy *= G_MAX / m;
      }
      this.gTrail[this.gHead * 2] = gx;
      this.gTrail[this.gHead * 2 + 1] = gy;
      this.gHead = (this.gHead + 1) % G_TRAIL;
      this.gCount = Math.min(G_TRAIL, this.gCount + 1);
      this.drawG(gx, gy);
    }
    if (this.refit(this.progressBar, this.toggles.progress)) L.prog = -1;
    const p = Math.round(Math.max(0, Math.min(1, (f.s - this.startS) / (this.finishS - this.startS))) * 400) / 400;
    if (this.toggles.progress && p !== L.prog) {
      L.prog = p;
      this.drawProgress(p);
    }
    if (this.splitUntil && f.now > this.splitUntil) {
      this.splitUntil = 0;
      this.splitRow.classList.remove('on');
    }
    if (this.bannerUntil && f.now > this.bannerUntil) {
      this.bannerUntil = 0;
      this.bannerEl.classList.remove('on');
    }
  }

  /** Start lights: `lit` red lamps (0–5), or all green. */
  setLights(lit: number, green: boolean, visible: boolean): void {
    this.lights.classList.toggle('on', visible);
    for (let i = 0; i < this.lamps.length; i++) {
      this.lamps[i].classList.toggle('red', !green && i < lit);
      this.lamps[i].classList.toggle('green', green);
    }
  }

  /** Split crossed: time and the delta to the personal best (null without one). */
  split(index: number, time: number, delta: number | null, now: number): void {
    if (!this.toggles.splits) return;
    this.splitLabel.textContent = `SPLIT ${index + 1}`;
    this.splitValue.textContent = delta === null ? formatTime(time) : formatDelta(delta);
    this.splitValue.className = `hud-split-value${delta === null ? '' : delta <= 0 ? ' good' : ' bad'}`;
    this.splitRow.classList.add('on');
    this.splitUntil = now + 6000;
  }

  banner(text: string, kind: 'info' | 'good' | 'bad', now: number, ms = 2500): void {
    this.bannerEl.textContent = text;
    this.bannerEl.className = `hud-banner on ${kind}`;
    this.bannerUntil = now + ms;
  }

  /** Penalty or event notice (stacked, fades on its own). */
  notice(text: string): void {
    if (!this.toggles.penalties) return;
    const n = el('div', 'hud-notice', this.notices, text);
    setTimeout(() => n.classList.add('out'), 3200);
    setTimeout(() => n.remove(), 3700);
  }

  /** Persistent hint (e.g. "Press R to reset"); null hides it. */
  prompt(text: string | null): void {
    if (text === null) {
      this.promptEl.classList.remove('on');
      return;
    }
    if (this.promptEl.textContent !== text) this.promptEl.textContent = text;
    this.promptEl.classList.add('on');
  }

  reset(): void {
    this.splitRow.classList.remove('on');
    this.bannerEl.classList.remove('on');
    this.splitUntil = this.bannerUntil = 0;
    this.notices.textContent = '';
    this.prompt(null);
    this.setLights(0, false, false);
    this.last.pen = '';
    this.last.cs = this.last.speedV = this.last.rev = this.last.led = this.last.prog = -1;
    this.last.gearI = -2;
  }
}
